// 상품 관리 페이지.
// 카테고리/검색 필터 + 페이지네이션 + 다중 선택 + 일괄 처리 (가격/품절/노출/베스트).
// 단일 행에는 빠른 토글(품절/노출) 과 수정 페이지 이동 링크를 둔다.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ListOrdered, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { resolveUnitLabel, type ProductDto } from "@oo/shared";
import { Badge, Button, useConfirm } from "@oo/ui";
import { DataTable, PageHeader, Pagination, TableMessage, Td, Th, Toolbar } from "@/shared/ui";
import {
  bulkPatchProducts,
  deleteProduct,
  listCategoriesAdmin,
  listProductsAdmin,
  listUnitsAdmin,
  reorderProducts,
  updateProduct,
} from "@/features/catalog/catalogApi.ts";
import ProductReorderList from "@/features/catalog/ProductReorderList.tsx";
import { imageUrl } from "@/features/catalog/imageUrl.ts";

const PAGE_SIZE = 30;

// 상품 목록 관리 화면 (검색/품절·노출 일괄변경).
export default function ProductsPage() {
  const qc = useQueryClient();
  const [categoryId, setCategoryId] = useState<string>("");
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // 카테고리 진열 순서 변경 모달 표시 여부.
  const [reorderOpen, setReorderOpen] = useState(false);

  const { data: categories = [] } = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: listCategoriesAdmin,
  });
  // 단위 라벨 표시용(DB 관리). 목록 행에 코드 대신 라벨을 보여준다.
  const { data: units = [] } = useQuery({
    queryKey: ["admin", "units"],
    queryFn: listUnitsAdmin,
  });
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "products", { categoryId, q: appliedQ, page }],
    queryFn: () =>
      listProductsAdmin({
        categoryId: categoryId || undefined,
        q: appliedQ || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  });

  const products = data?.products ?? [];
  const total = data?.total ?? 0;

  const allSelectedOnPage = useMemo(
    () => products.length > 0 && products.every((p) => selected.has(p.id)),
    [products, selected],
  );

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function togglePageSelect() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        for (const p of products) next.delete(p.id);
      } else {
        for (const p of products) next.add(p.id);
      }
      return next;
    });
  }

  // 일괄 처리.
  const bulkMut = useMutation({
    mutationFn: bulkPatchProducts,
    onSuccess: async () => {
      setSelected(new Set());
      await qc.invalidateQueries({ queryKey: ["admin", "products"] });
    },
  });
  function runBulkSoldOut(value: boolean) {
    if (selected.size === 0) return;
    bulkMut.mutate({ productIds: [...selected], patch: { isSoldOut: value } });
  }
  function runBulkVisible(value: boolean) {
    if (selected.size === 0) return;
    bulkMut.mutate({ productIds: [...selected], patch: { isVisible: value } });
  }
  function runBulkBest(value: boolean) {
    if (selected.size === 0) return;
    bulkMut.mutate({ productIds: [...selected], patch: { isBest: value } });
  }
  function runBulkPrice() {
    if (selected.size === 0) return;
    const raw = window.prompt("선택한 상품들의 새 가격(원, 정수)을 입력하세요.");
    if (!raw) return;
    const price = Number.parseInt(raw, 10);
    if (!Number.isFinite(price) || price < 0) {
      window.alert("유효한 가격을 입력해 주세요.");
      return;
    }
    bulkMut.mutate({ productIds: [...selected], patch: { priceKrw: price } });
  }

  return (
    <div className="mx-auto flex max-w-screen-xl flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title="상품"
        description="카테고리/검색으로 상품을 찾고, 선택해 품절·노출·가격을 일괄 변경합니다."
        actions={
          <>
            <Link
              to="/catalog/merchandising"
              className="inline-flex min-h-touch items-center gap-2 rounded border border-ink-300 px-4 text-sm font-semibold text-ink-700 transition-colors duration-fast hover:bg-ink-100"
            >
              <ListOrdered size={18} aria-hidden="true" />
              진열 관리
            </Link>
            <Link
              to="/catalog/products/new"
              className="inline-flex min-h-touch items-center gap-2 rounded bg-brand-600 px-4 text-sm font-semibold text-white transition-colors duration-fast hover:bg-brand-700"
            >
              <Plus size={18} aria-hidden="true" />
              신규 상품 등록
            </Link>
          </>
        }
      />

      <Toolbar>
        <label className="flex w-full flex-col gap-1 text-xs font-medium text-ink-600 sm:w-auto">
          카테고리
          <select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setPage(0);
            }}
            className="min-h-touch rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900"
          >
            <option value="">전체</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 basis-full flex-col gap-1 text-xs font-medium text-ink-600 sm:basis-auto">
          상품명 검색
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setAppliedQ(q.trim());
                setPage(0);
              }
            }}
            placeholder="상품명 일부"
            className="min-h-touch min-w-0 rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900 placeholder:text-ink-500"
          />
        </label>
        <Button
          variant="secondary"
          onClick={() => {
            setAppliedQ(q.trim());
            setPage(0);
          }}
        >
          검색
        </Button>
        {/* 특정 카테고리를 골랐을 때만 그 카테고리의 진열 순서를 드래그로 바꿀 수 있다. */}
        {categoryId ? (
          <Button variant="secondary" onClick={() => setReorderOpen(true)}>
            진열 순서 변경
          </Button>
        ) : null}
      </Toolbar>

      {selected.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm text-white">
          <span className="font-semibold">선택 {selected.size}개</span>
          <BulkButton onClick={() => runBulkSoldOut(true)}>품절로</BulkButton>
          <BulkButton onClick={() => runBulkSoldOut(false)}>판매중</BulkButton>
          <BulkButton onClick={() => runBulkVisible(false)}>숨김</BulkButton>
          <BulkButton onClick={() => runBulkVisible(true)}>노출</BulkButton>
          <BulkButton onClick={() => runBulkBest(true)}>베스트 지정</BulkButton>
          <BulkButton onClick={() => runBulkBest(false)}>베스트 해제</BulkButton>
          <BulkButton onClick={runBulkPrice}>가격 일괄 변경</BulkButton>
          <BulkButton onClick={() => setSelected(new Set())} className="ml-auto">
            선택 해제
          </BulkButton>
        </div>
      ) : null}

      {/* 좁은 화면에서는 가로 표 대신 터치하기 쉬운 카드 목록으로 표시한다. */}
      <section className="flex flex-col gap-2 md:hidden" aria-label="상품 모바일 목록">
        <label className="flex min-h-touch items-center gap-2 rounded-lg border border-ink-200 bg-surface px-3 text-sm font-medium text-ink-700">
          <input
            type="checkbox"
            checked={allSelectedOnPage}
            onChange={togglePageSelect}
            aria-label="이 페이지의 모든 상품 선택"
          />
          이 페이지 전체 선택
        </label>
        {isLoading ? (
          <p className="rounded-lg border border-ink-200 bg-surface p-6 text-center text-sm text-ink-600">
            불러오는 중...
          </p>
        ) : products.length === 0 ? (
          <p className="rounded-lg border border-ink-200 bg-surface p-6 text-center text-sm text-ink-600">
            결과가 없습니다.
          </p>
        ) : (
          products.map((p) => (
            <ProductMobileCard
              key={p.id}
              product={p}
              selected={selected.has(p.id)}
              onToggle={() => toggleSelect(p.id)}
              categoryName={categories.find((c) => c.id === p.categoryId)?.name ?? ""}
              unitLabel={resolveUnitLabel(units, p.unit, p.packWeightG)}
            />
          ))
        )}
      </section>

      <div className="hidden md:block">
        <DataTable
          head={
            <tr>
              <Th className="w-10">
                <input
                  type="checkbox"
                  checked={allSelectedOnPage}
                  onChange={togglePageSelect}
                  aria-label="이 페이지의 모든 상품 선택"
                />
              </Th>
              <Th className="w-16">이미지</Th>
              <Th>상품명</Th>
              <Th>카테고리</Th>
              <Th>가격</Th>
              <Th>단위</Th>
              <Th>상태</Th>
              <Th>관리</Th>
            </tr>
          }
        >
          {isLoading ? (
            <TableMessage colSpan={8}>불러오는 중...</TableMessage>
          ) : products.length === 0 ? (
            <TableMessage colSpan={8}>결과가 없습니다.</TableMessage>
          ) : (
            products.map((p) => (
              <ProductRow
                key={p.id}
                product={p}
                selected={selected.has(p.id)}
                onToggle={() => toggleSelect(p.id)}
                categoryName={categories.find((c) => c.id === p.categoryId)?.name ?? ""}
                unitLabel={resolveUnitLabel(units, p.unit, p.packWeightG)}
              />
            ))
          )}
        </DataTable>
      </div>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />

      {/* 카테고리 진열 순서 변경 모달 */}
      {reorderOpen && categoryId ? (
        <CategoryReorderModal
          categoryId={categoryId}
          categoryName={categories.find((c) => c.id === categoryId)?.name ?? "카테고리"}
          onClose={() => setReorderOpen(false)}
        />
      ) : null}
    </div>
  );
}

// 카테고리 진열 순서 변경 모달.
// 해당 카테고리의 상품(숨김/품절 포함)을 카테고리별 진열 순서대로 불러와 드래그로 재배열 후 저장한다.
function CategoryReorderModal({
  categoryId,
  categoryName,
  onClose,
}: {
  categoryId: string;
  categoryName: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "category-reorder", categoryId],
    queryFn: () => listProductsAdmin({ categoryId, limit: 100, offset: 0 }).then((r) => r.products),
  });
  const [working, setWorking] = useState<ProductDto[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (query.data) {
      setWorking(query.data);
      setDirty(false);
    }
  }, [query.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      reorderProducts({
        scope: "category",
        categoryId,
        orderedIds: working.map((p) => p.id),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin", "products"] });
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="카테고리 진열 순서 변경"
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-surface shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
          <h2 className="text-base font-bold text-ink-900">{categoryName} 진열 순서</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm text-ink-600 hover:bg-ink-100"
          >
            닫기
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {query.isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-14 animate-pulse rounded-md bg-ink-100" />
              ))}
            </div>
          ) : (
            <ProductReorderList
              items={working}
              onReorder={(next) => {
                setWorking(next);
                setDirty(true);
              }}
            />
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-ink-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ink-300 px-4 py-2 text-sm hover:bg-ink-100"
          >
            취소
          </button>
          <button
            type="button"
            disabled={!dirty || saveMut.isPending}
            onClick={() => saveMut.mutate()}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-40"
          >
            {saveMut.isPending ? "저장 중..." : "순서 저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

// 일괄 처리 바 내부의 반투명 버튼(브랜드 배경 위).
function BulkButton({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded bg-white/15 px-2 py-1 font-medium hover:bg-white/25 ${className ?? ""}`}
    >
      {children}
    </button>
  );
}

function ProductRow({
  product,
  selected,
  onToggle,
  categoryName,
  unitLabel,
}: {
  product: ProductDto;
  selected: boolean;
  onToggle: () => void;
  categoryName: string;
  unitLabel: string;
}) {
  const { confirm, toggleSoldOut, toggleVisible, removeMut } = useProductActions(product);
  const url = imageUrl(product.mainImageKey);

  return (
    <tr className={selected ? "bg-brand-50" : undefined}>
      <Td>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`${product.name} 선택`}
        />
      </Td>
      <Td>
        <div className="h-10 w-10 overflow-hidden rounded border border-ink-200 bg-ink-50">
          {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
        </div>
      </Td>
      <Td className="font-medium text-ink-900">
        <Link to={`/catalog/products/${product.id}`} className="hover:underline">
          {product.name}
        </Link>
        {product.isBest ? (
          <Badge tone="warning" className="ml-1.5">
            베스트
          </Badge>
        ) : null}
      </Td>
      <Td className="text-ink-700">{categoryName}</Td>
      <Td className="tabular-nums text-ink-900">{product.priceKrw.toLocaleString()}원</Td>
      <Td className="text-ink-700">{unitLabel}</Td>
      <Td>
        <div className="flex flex-wrap gap-1">
          <Badge tone={product.isVisible ? "success" : "neutral"}>
            {product.isVisible ? "노출" : "숨김"}
          </Badge>
          <Badge tone={product.isSoldOut ? "danger" : "neutral"}>
            {product.isSoldOut ? "품절" : "판매중"}
          </Badge>
        </div>
      </Td>
      <Td>
        <div className="flex flex-wrap gap-1 text-xs">
          <RowButton onClick={() => toggleSoldOut.mutate()}>
            {product.isSoldOut ? "판매재개" : "품절"}
          </RowButton>
          <RowButton onClick={() => toggleVisible.mutate()}>
            {product.isVisible ? "숨기기" : "노출"}
          </RowButton>
          <Link
            to={`/catalog/products/${product.id}`}
            className="rounded border border-ink-300 px-2 py-1 hover:bg-ink-100"
          >
            수정
          </Link>
          <button
            type="button"
            onClick={async () => {
              const ok = await confirm({
                title: "상품 삭제",
                description: `'${product.name}' 을 삭제하시겠습니까?`,
                confirmLabel: "삭제",
              });
              if (ok) {
                removeMut.mutate();
              }
            }}
            className="rounded border border-ink-300 px-2 py-1 text-danger-700 hover:bg-danger-50"
          >
            삭제
          </button>
        </div>
      </Td>
    </tr>
  );
}

// 모바일 상품 카드. 표의 모든 상태와 관리 동작을 세로 흐름으로 재배치한다.
function ProductMobileCard({
  product,
  selected,
  onToggle,
  categoryName,
  unitLabel,
}: {
  product: ProductDto;
  selected: boolean;
  onToggle: () => void;
  categoryName: string;
  unitLabel: string;
}) {
  const { confirm, toggleSoldOut, toggleVisible, removeMut } = useProductActions(product);
  const url = imageUrl(product.mainImageKey);

  return (
    <article
      className={`min-w-0 rounded-lg border p-3 shadow-card ${
        selected ? "border-brand-600 bg-brand-50" : "border-ink-200 bg-surface"
      }`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`${product.name} 선택`}
          className="mt-1 shrink-0"
        />
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded border border-ink-200 bg-ink-50">
          {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
        </div>
        <div className="min-w-0 flex-1">
          <Link
            to={`/catalog/products/${product.id}`}
            className="block break-words font-semibold text-ink-900 hover:underline"
          >
            {product.name}
          </Link>
          <p className="mt-1 text-xs text-ink-600">{categoryName || "카테고리 없음"}</p>
          <p className="mt-1 text-sm font-semibold tabular-nums text-ink-900">
            {product.priceKrw.toLocaleString()}원 · {unitLabel}
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        {product.isBest ? <Badge tone="warning">베스트</Badge> : null}
        <Badge tone={product.isVisible ? "success" : "neutral"}>
          {product.isVisible ? "노출" : "숨김"}
        </Badge>
        <Badge tone={product.isSoldOut ? "danger" : "neutral"}>
          {product.isSoldOut ? "품절" : "판매중"}
        </Badge>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <RowButton onClick={() => toggleSoldOut.mutate()}>
          {product.isSoldOut ? "판매재개" : "품절"}
        </RowButton>
        <RowButton onClick={() => toggleVisible.mutate()}>
          {product.isVisible ? "숨기기" : "노출"}
        </RowButton>
        <Link
          to={`/catalog/products/${product.id}`}
          className="rounded border border-ink-300 px-2 py-2 text-center hover:bg-ink-100"
        >
          수정
        </Link>
        <button
          type="button"
          onClick={async () => {
            const ok = await confirm({
              title: "상품 삭제",
              description: `'${product.name}' 을 삭제하시겠습니까?`,
              confirmLabel: "삭제",
            });
            if (ok) removeMut.mutate();
          }}
          className="rounded border border-ink-300 px-2 py-2 text-danger-700 hover:bg-danger-50"
        >
          삭제
        </button>
      </div>
    </article>
  );
}

// 데스크톱 행과 모바일 카드가 같은 변경·삭제 동작을 공유한다.
function useProductActions(product: ProductDto) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const toggleSoldOut = useMutation({
    mutationFn: () => updateProduct(product.id, { isSoldOut: !product.isSoldOut }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "products"] }),
  });
  const toggleVisible = useMutation({
    mutationFn: () => updateProduct(product.id, { isVisible: !product.isVisible }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "products"] }),
  });
  const removeMut = useMutation({
    mutationFn: () => deleteProduct(product.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "products"] }),
  });
  return { confirm, toggleSoldOut, toggleVisible, removeMut };
}

// 행 내 작은 보조 액션 버튼(토큰 테두리).
function RowButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded border border-ink-300 px-2 py-1 hover:bg-ink-100"
    >
      {children}
    </button>
  );
}
